import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import CustomModal from './CustomModal';
import { submitFeedback } from '../../../api';
import { useHideOnScroll } from '../hooks/useHideOnScroll';

// bottom: 78px stacks this above the AI chat launcher (.buttonStyle in toolbar.css),
// which sits at the same right:20px/bottom:20px corner (48px tall) and would
// otherwise render on top of this button since it has a higher z-index.
const FloatingButton = styled.button`
  position: fixed;
  right: 20px;
  bottom: 78px;
  z-index: 105;
  height: 48px;
  width: 48px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  opacity: 0.85;
  overflow: hidden;
  white-space: nowrap;
  transition: opacity 0.2s ease, width 0.25s ease;
  &:hover,
  &:focus-visible {
    opacity: 1;
    width: 116px;
  }
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
`;

// Text is collapsed to zero width and slides open on hover/focus; the button is
// right-anchored so it grows leftward without moving the icon.
const Label = styled.span`
  display: inline-block;
  max-width: 0;
  opacity: 0;
  margin-left: 0;
  font-size: 0.85rem;
  overflow: hidden;
  transition: max-width 0.25s ease, opacity 0.2s ease, margin-left 0.25s ease;
  ${FloatingButton}:hover &,
  ${FloatingButton}:focus-visible & {
    max-width: 70px;
    opacity: 1;
    margin-left: 6px;
  }
`;

const StarRow = styled.div`
  font-size: 2rem;
  cursor: pointer;
  i {
    margin-right: 6px;
    color: var(--bs-gray-400);
  }
  i.filled {
    color: var(--bs-warning);
  }
`;

const emptyForm = { rating: 0, comment: '', feature: '' };

/**
 * Global, mounted-once feedback capture entry point (MBAI-585). context.screen
 * is auto-derived from the current route so every submission is screen-tagged
 * without per-page instrumentation; "feature" is an optional free-text tag the
 * user can add for a more specific callout. Leaving both blank behaves as
 * "overall" feedback for that screen.
 */
export const FeedbackWidget: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const isVisible = useHideOnScroll();

  const handleClose = () => {
    setIsOpen(false);
    setForm(emptyForm);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!form.rating) {
      toast.error('Please select a rating.'.toTranslatedDisplay());
      return;
    }
    setSubmitting(true);
    try {
      await submitFeedback({
        rating: form.rating,
        comment: form.comment.trim() || undefined,
        context: {
          screen: location.pathname,
          feature: form.feature.trim() || undefined,
        },
      });
      toast.success('Thanks for your feedback!'.toTranslatedDisplay());
      handleClose();
    } catch (error) {
      toast.error('Something went wrong submitting your feedback.'.toTranslatedDisplay());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {isVisible &&
        <FloatingButton
          type='button'
          className='btn btn-primary'
          onClick={() => setIsOpen(true)}
          title={'Feedback'.toTranslatedDisplay()}
          aria-label={'Feedback'.toTranslatedDisplay()}
        >
          <i className='bi bi-chat-square-heart-fill'></i>
          <Label>{'Feedback'.toTranslatedDisplay()}</Label>
        </FloatingButton>
      }

      <CustomModal
        id='feedback-widget-modal'
        title={'Share Your Feedback'.toTranslatedDisplay()}
        isVisible={isOpen}
        onClose={handleClose}
        size='medium'
        body={
          <div>
            <div className='mb-4'>
              <label className='form-label d-block'>{'How would you rate your experience?'.toTranslatedDisplay()}</label>
              <StarRow>
                {[1, 2, 3, 4, 5].map((value) => (
                  <i
                    key={value}
                    className={`bi ${value <= form.rating ? 'bi-star-fill filled' : 'bi-star'}`}
                    onClick={() => setForm((prev) => ({ ...prev, rating: value }))}
                    role='button'
                    aria-label={`${value} ${'star'.toTranslatedDisplay()}`}
                  ></i>
                ))}
              </StarRow>
            </div>
            <div className='mb-4'>
              <label className='form-label'>{'Related to (optional)'.toTranslatedDisplay()}</label>
              <input
                type='text'
                className='form-control'
                placeholder={'e.g. invoicing, reports, mobile app'.toTranslatedDisplay()}
                value={form.feature}
                onChange={(e) => setForm((prev) => ({ ...prev, feature: e.target.value }))}
              />
            </div>
            <div className='mb-2'>
              <label className='form-label'>{'Comments (optional)'.toTranslatedDisplay()}</label>
              <textarea
                className='form-control'
                rows={3}
                placeholder={'Tell us more...'.toTranslatedDisplay()}
                value={form.comment}
                onChange={(e) => setForm((prev) => ({ ...prev, comment: e.target.value }))}
              />
            </div>
          </div>
        }
        showFooter
        onSave={handleSubmit}
      />
    </>
  );
};

export default FeedbackWidget;
